import React, { useRef, useMemo, useState, useEffect } from 'react'
import { useFrame, extend, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { Stars, shaderMaterial, Float, Environment } from '@react-three/drei'
import { v4 as uuidv4 } from 'uuid'

// --- SHADERS ---







// Shader de Nebulosa (Background Procedural)
const NebulaMaterial = shaderMaterial(
  { uTime: 0, uColor1: new THREE.Color('#000000'), uColor2: new THREE.Color('#0a001a') }, // Preto -> Roxo Profundo
  `
    varying vec2 vUv;
    varying vec3 vPosition;
    void main() { 
      vUv = uv; 
      vPosition = position;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); 
    }
  `,
  `
    uniform float uTime;
    uniform vec3 uColor1;
    uniform vec3 uColor2;
    varying vec3 vPosition;
    
    // Simplex Noise 3D function (simplified)
    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
    vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
    
    float snoise(vec3 v) { 
      const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
      const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
      vec3 i  = floor(v + dot(v, C.yyy) );
      vec3 x0 = v - i + dot(i, C.xxx) ;
      vec3 g = step(x0.yzx, x0.xyz);
      vec3 l = 1.0 - g;
      vec3 i1 = min( g.xyz, l.zxy );
      vec3 i2 = max( g.xyz, l.zxy );
      vec3 x1 = x0 - i1 + C.xxx;
      vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y
      vec3 x3 = x0 - D.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y
      i = mod289(i); 
      vec4 p = permute( permute( permute( 
                 i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
               + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
               + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
      float n_ = 0.142857142857; // 1.0/7.0
      vec3  ns = n_ * D.wyz - D.xzx;
      vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,N)
      vec4 x_ = floor(j * ns.z);
      vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)
      vec4 x = x_ *ns.x + ns.yyyy;
      vec4 y = y_ *ns.x + ns.yyyy;
      vec4 h = 1.0 - abs(x) - abs(y);
      vec4 b0 = vec4( x.xy, y.xy );
      vec4 b1 = vec4( x.zw, y.zw );
      vec4 s0 = floor(b0)*2.0 + 1.0;
      vec4 s1 = floor(b1)*2.0 + 1.0;
      vec4 sh = -step(h, vec4(0.0));
      vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
      vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
      vec3 p0 = vec3(a0.xy,h.x);
      vec3 p1 = vec3(a0.zw,h.y);
      vec3 p2 = vec3(a1.xy,h.z);
      vec3 p3 = vec3(a1.zw,h.w);
      vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
      p0 *= norm.x;
      p1 *= norm.y;
      p2 *= norm.z;
      p3 *= norm.w;
      vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
      m = m * m;
      return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                    dot(p2,x2), dot(p3,x3) ) );
    }

    void main() {
      // Noise lento baseado na posição e tempo
      float n = snoise(vPosition * 0.02 + uTime * 0.05);
      
      // Mapear noise para mix de cores
      // n varia de -1 a 1. Vamos normalizar para 0 a 1 e aumentar o contraste
      float intensity = smoothstep(0.2, 0.8, n * 0.5 + 0.5);
      
      vec3 color = mix(uColor1, uColor2, intensity * 0.4); // Mistura sutil
      
      // Adicionar estrelas distantes cintilantes via noise de alta frequência
      float starNoise = snoise(vPosition * 0.8);
      float stars = step(0.98, starNoise) * (sin(uTime * 2.0 + vPosition.x) * 0.5 + 0.5);
      
      gl_FragColor = vec4(color + vec3(stars), 1.0);
    }
  `
)
extend({ NebulaMaterial })

// Shader de Grade Cyberpunk (Synthwave/Tron Style)
const CyberGridMaterial = shaderMaterial(
  { uTime: 0, uColor: new THREE.Color('#ff00ff'), uColor2: new THREE.Color('#00ffff') },
  `
    varying vec2 vUv;
    varying vec3 vPos;
    void main() { 
      vUv = uv; 
      vPos = position;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); 
    }
  `,
  `
    uniform float uTime;
    uniform vec3 uColor;
    uniform vec3 uColor2;
    varying vec2 vUv;
    varying vec3 vPos;

    void main() {
      // Coordenadas de grade em movimento
      vec2 gridUv = vUv * 50.0;
      gridUv.y += uTime * 0.5; // Movimento para frente

      // Desenhar linhas da grade
      float thickness = 0.05;
      float x = step(1.0 - thickness, fract(gridUv.x));
      float y = step(1.0 - thickness, fract(gridUv.y));
      float grid = max(x, y);

      // Fade na distância (horizonte)
      float dist = distance(vUv, vec2(0.5, 0.0)); // Centro baixo
      float fade = 1.0 - smoothstep(0.0, 0.8, vUv.y); 

      // Cor varia com a posição (efeito degradê cyberpunk)
      vec3 finalColor = mix(uColor, uColor2, vUv.y + sin(uTime) * 0.2);
      
      float alpha = grid * fade * 0.5; // Transparência base

      gl_FragColor = vec4(finalColor, alpha);
    }
  `
)
extend({ CyberGridMaterial })

const TrailMaterial = shaderMaterial(
  { uTime: 0, uColor: new THREE.Color('#00ffff'), uOpacity: 1.0 },
  `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  `
    uniform float uTime;
    uniform vec3 uColor;
    uniform float uOpacity;
    varying vec2 vUv;
    
    void main() {
      // Gradiente suave nas pontas (fade in/out)
      float fade = smoothstep(0.0, 0.2, vUv.x) * smoothstep(1.0, 0.4, vUv.x);
      
      // Centro mais brilhante (core)
      float core = 1.0 - abs(vUv.y - 0.5) * 2.0;
      core = pow(core, 3.0); // Mais fino e intenso
      
      // Simulação de turbulência simples via seno
      float turbulence = sin(vUv.x * 20.0 - uTime * 15.0) * 0.1;
      
      float alpha = (fade * core) + turbulence;
      alpha *= uOpacity;
      
      // Cor aditiva (mais branco no centro)
      vec3 finalColor = mix(uColor, vec3(1.0), core * 0.5);
      
      gl_FragColor = vec4(finalColor, alpha);
    }
  `
)
extend({ TrailMaterial })

function NebulaSphere() {
  const ref = useRef<any>()
  const { camera } = useThree()

  useFrame((state) => {
    // @ts-ignore
    if (ref.current && ref.current.material) {
      // @ts-ignore
      ref.current.material.uniforms.uTime.value = state.clock.elapsedTime

      // Fazer o fundo seguir a câmera no eixo Y para cobrir "toda a cena lá para baixo"
      ref.current.position.copy(camera.position)
    }
  })

  return (
    <mesh ref={ref}>
      <sphereGeometry args={[800, 32, 32]} />
      {/* Aumentei raio de 300 para 800 para garantir cobertura total */}
      {/* @ts-ignore */}
      <nebulaMaterial side={THREE.BackSide} />
    </mesh>
  )
}

function CyberGrid() {
  const ref = useRef<any>()
  useFrame((state) => {
    if (ref.current) {
      ref.current.uTime = state.clock.elapsedTime
    }
  })

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -20, -50]}>
      <planeGeometry args={[300, 300]} />
      {/* @ts-ignore */}
      <cyberGridMaterial ref={ref} transparent blending={THREE.AdditiveBlending} depthWrite={false} />
    </mesh>
  )
}

// --- TIPOS DE EVENTOS ---
type EventType = 'shooting_star' | 'comet' | 'satellite' | 'ufo'

interface SpaceEvent {
  id: string
  type: EventType
  startPos: THREE.Vector3
  endPos: THREE.Vector3
  startTime: number
  duration: number
  extra?: any
}

export function CinematicBackground() {
  const { camera, scene } = useThree()

  return (
    <group>
      {/* 1. Base: Cyberpunk Space & Nebula */}
      <color attach="background" args={['#050011']} />
      <fog attach="fog" args={['#050011', 40, 200]} />
      <NebulaSphere />
      <CyberGrid />

      {/* Luzes Tech: Dualidade Ciano/Magenta */}
      <ambientLight intensity={0.2} color="#001133" />
      <spotLight position={[-30, 20, 10]} intensity={2} color="#00ffff" angle={0.5} penumbra={1} />
      <spotLight position={[30, -20, -10]} intensity={2} color="#ff00ff" angle={0.5} penumbra={1} />

      {/* 2. Poeira Digital (Pixels) */}
      <SpaceDust />

      {/* 3. Eventos Aleatórios (Vida no Espaço) */}
      <RandomEventsManager />

      {/* 4. Estrelas Estáticas (Fundo Infinito) */}
      <group position={[0, 0, 0]}>
        {/* @ts-ignore */}
        <Stars radius={300} count={8000} factor={4} saturation={0} fade speed={0.05} />
      </group>

      {/* 5. Reflexos */}
      <Environment preset="city" blur={0.8} intensity={0.2} />
    </group>
  )
}





// --- NOVO COMPONENTE: POEIRA ESPACIAL (PIXELS CYBERPUNK) ---
function SpaceDust({ count = 400 }) {
  const mesh = useRef<THREE.InstancedMesh>(null)
  const { viewport, clock } = useThree()

  // Gerar posições iniciais e velocidades aleatórias
  const particles = useMemo(() => {
    const temp = []
    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 120 // Área mais ampla
      const y = (Math.random() - 0.5) * 80
      const z = (Math.random() - 0.5) * 60 - 10
      const speed = 0.05 + Math.random() * 0.1
      const factor = 0.2 + Math.random() * 0.8
      temp.push({ x, y, z, speed, factor, mx: 0, my: 0 })
    }
    return temp
  }, [count])

  const dummy = useMemo(() => new THREE.Object3D(), [])

  useFrame((state) => {
    if (!mesh.current) return
    const t = state.clock.getElapsedTime()

    particles.forEach((particle, i) => {
      const { factor, speed, x, y, z } = particle

      // Movimento mais "digital" (linear + noise)
      const tx = x + Math.sin(t * speed * 0.5) * 5
      const ty = y + Math.cos(t * speed * 0.3) * 5
      const tz = z + Math.sin(t * speed * 0.2) * 2

      dummy.position.set(tx, ty, tz)
      // Rotação rápida (efeito glitch/tech)
      dummy.rotation.set(t * speed * 2, t * speed * 2, 0)
      dummy.scale.setScalar(factor * 0.3)

      dummy.updateMatrix()
      mesh.current!.setMatrixAt(i, dummy.matrix)
    })

    mesh.current.instanceMatrix.needsUpdate = true
  })

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <boxGeometry args={[0.2, 0.2, 0.2]} />
      <meshBasicMaterial color="#00ffff" transparent opacity={0.8} blending={THREE.AdditiveBlending} />
    </instancedMesh>
  )
}

function RandomEventsManager() {
  const [events, setEvents] = useState<SpaceEvent[]>([])
  const nextSpawnTime = useRef(0)

  useFrame((state) => {
    const time = state.clock.elapsedTime

    // 1. Spawn Logic
    if (time > nextSpawnTime.current) {
      // Definir próximo spawn (aleatório entre 2s e 10s)
      nextSpawnTime.current = time + 2 + Math.random() * 8

      // Escolher tipo de evento (Pesos ajustados para dar chance de ver o UFO)
      const r = Math.random()
      let type: EventType = 'shooting_star'
      let duration = 1 // segundos

      if (r > 0.85) { type = 'ufo'; duration = 25; } // Aumentei chance do UFO para 15% (era 1%)
      else if (r > 0.75) { type = 'comet'; duration = 5; }
      else if (r > 0.50) { type = 'satellite'; duration = 20; }
      else { type = 'shooting_star'; duration = 0.8; }

      // Gerar coordenadas de entrada e saída (fora da tela -> fora da tela)
      // Range X: -80 a 80, Y: -40 a 40, Z: -20 a -60
      const startX = (Math.random() - 0.5) * 160
      const startY = (Math.random() - 0.5) * 80
      const startZ = -30 - Math.random() * 50

      const endX = startX + (Math.random() - 0.5) * 100 // Move-se para algum lado
      const endY = startY + (Math.random() - 0.5) * 50

      const newEvent: SpaceEvent = {
        id: uuidv4(),
        type,
        startPos: new THREE.Vector3(startX, startY, startZ),
        endPos: new THREE.Vector3(endX, endY, startZ), // Mantém Z aprox
        startTime: time,
        duration,
        extra: { color: type === 'ufo' ? '#00ff00' : '#ffffff' }
      }

      setEvents(prev => [...prev, newEvent])
    }

    // 2. Cleanup Logic (Remove eventos expirados)
    setEvents(prev => prev.filter(e => time < e.startTime + e.duration + 1)) // +1s buffer
  })

  return (
    <group>
      {events.map(ev => (
        <SpaceEventItem key={ev.id} event={ev} />
      ))}
    </group>
  )
}

function SpaceEventItem({ event }: { event: SpaceEvent }) {
  const meshRef = useRef<THREE.Group>(null)
  const trailRef = useRef<any>(null)

  useFrame((state) => {
    if (!meshRef.current) return
    const time = state.clock.elapsedTime
    const age = time - event.startTime
    const progress = Math.min(age / event.duration, 1)

    // Interpolação Linear de Posição
    meshRef.current.position.lerpVectors(event.startPos, event.endPos, progress)

    // Atualizar uniforms do Trail se existir
    if (trailRef.current) {
      trailRef.current.uTime = time
    }

    // Comportamentos Específicos
    if (event.type === 'ufo') {
      // UFO: Movimento flutuante e rotação do disco
      meshRef.current.rotation.y += 0.005
      // Oscilação vertical suave
      meshRef.current.position.y += Math.sin(time * 0.5) * 0.03
    } else if (event.type === 'satellite') {
      // Satélite estabilizado, gira muito lentamente
      meshRef.current.rotation.y += 0.005
      // Oscilação leve
      meshRef.current.position.y += Math.sin(time * 2) * 0.02
    }

    // Fade out no final
    if (progress > 0.9) {
      const fade = 1.0 - (progress - 0.9) * 10
      meshRef.current.scale.setScalar(fade)
    }
  })

  // Orientação baseada na direção do movimento
  const directionRotation = useMemo(() => {
    return new THREE.Euler(0, 0, Math.atan2(event.endPos.y - event.startPos.y, event.endPos.x - event.startPos.x))
  }, [event.startPos, event.endPos])

  // --- RENDERIZADORES ---

  if (event.type === 'shooting_star') {
    return (
      <group ref={meshRef}>
        <group rotation={directionRotation}>
          {/* Cabeça Brilhante */}
          <mesh position={[0, 0, 0]}>
            <sphereGeometry args={[0.3, 8, 8]} />
            <meshBasicMaterial color="#ffffff" />
          </mesh>
          {/* Rastro Longo e Rápido */}
          <mesh position={[-4, 0, 0]}>
            <planeGeometry args={[8, 0.4]} />
            {/* @ts-ignore */}
            <trailMaterial ref={trailRef} uColor={new THREE.Color('#aaddff')} transparent side={THREE.DoubleSide} />
          </mesh>
        </group>
      </group>
    )
  }

  if (event.type === 'comet') {
    return (
      <group ref={meshRef}>
        <group rotation={directionRotation}>
          {/* Núcleo de Gelo/Rocha */}
          <mesh>
            <sphereGeometry args={[0.6, 16, 16]} />
            <meshStandardMaterial color="#ccffff" emissive="#0044aa" emissiveIntensity={2} toneMapped={false} />
          </mesh>
          {/* Halo ao redor do núcleo */}
          <mesh>
            <sphereGeometry args={[1.2, 16, 16]} />
            <meshBasicMaterial color="#00ffff" transparent opacity={0.2} />
          </mesh>
          {/* Cauda Volumosa (Trail Shader) */}
          <mesh position={[-6, 0, 0]}>
            <planeGeometry args={[12, 1.5]} />
            {/* @ts-ignore */}
            <trailMaterial ref={trailRef} uColor={new THREE.Color('#00ffff')} uOpacity={0.6} transparent side={THREE.DoubleSide} />
          </mesh>
        </group>
      </group>
    )
  }

  if (event.type === 'satellite') {
    return (
      <group ref={meshRef}>
        {/* Corpo Tech */}
        <mesh>
          <boxGeometry args={[0.8, 0.8, 0.8]} />
          <meshStandardMaterial color="#222" metalness={0.9} roughness={0.2} />
        </mesh>
        {/* Detalhe Branding (Anel Neon Pequeno) */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.6, 0.05, 16, 32]} />
          <meshBasicMaterial color="#00ffff" toneMapped={false} />
        </mesh>
        {/* Painéis Solares (Mais detalhados) */}
        <group position={[1.2, 0, 0]}>
          <mesh>
            <boxGeometry args={[1.5, 0.05, 0.6]} />
            <meshStandardMaterial color="#111" metalness={0.5} roughness={0.8} />
          </mesh>
          <mesh position={[0, 0.03, 0]}>
            <planeGeometry args={[1.4, 0.5]} rotation={[-Math.PI / 2, 0, 0]} />
            <meshBasicMaterial color="#0033aa" />
          </mesh>
        </group>
        <group position={[-1.2, 0, 0]}>
          <mesh>
            <boxGeometry args={[1.5, 0.05, 0.6]} />
            <meshStandardMaterial color="#111" metalness={0.5} roughness={0.8} />
          </mesh>
          <mesh position={[0, 0.03, 0]}>
            <planeGeometry args={[1.4, 0.5]} rotation={[-Math.PI / 2, 0, 0]} />
            <meshBasicMaterial color="#0033aa" />
          </mesh>
        </group>

        {/* Luz de Navegação */}
        <PointBlinker color="#ff0000" speed={3} />
        <PointBlinker color="#ffffff" speed={1} />
      </group>
    )
  }

  if (event.type === 'ufo') {
    // Disco Voador Clássico com Luz de Abdução Verde
    return (
      <group ref={meshRef}>
        {/* Domo de Vidro (Cima) */}
        <mesh position={[0, 0.3, 0]}>
          <sphereGeometry args={[0.5, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshPhysicalMaterial
            color="#88ccff"
            roughness={0}
            metalness={0.9}
            transparent
            opacity={0.6}
            transmission={0.5}
            thickness={1}
          />
        </mesh>

        {/* Disco Metálico (Corpo) */}
        <mesh>
          <cylinderGeometry args={[1.5, 1.5, 0.2, 32]} />
          <meshStandardMaterial color="#silver" metalness={1} roughness={0.3} />
        </mesh>

        {/* Anel de Luzes Giratórias */}
        <mesh position={[0, 0, 0]}>
          <torusGeometry args={[1.55, 0.05, 16, 64]} />
          <meshBasicMaterial color="#00ff00" toneMapped={false} />
        </mesh>

        {/* Base do Disco (Baixo) */}
        <mesh position={[0, -0.2, 0]}>
          <cylinderGeometry args={[0.5, 1.2, 0.3, 32]} />
          <meshStandardMaterial color="#444" metalness={0.8} roughness={0.5} />
        </mesh>
      </group>
    )
  }

  return null
}

function PointBlinker({ color, speed }: { color: string, speed: number }) {
  const ref = useRef<any>(null)
  useFrame((state) => {
    if (ref.current) {
      const v = (Math.sin(state.clock.elapsedTime * speed * 10) + 1) / 2
      ref.current.intensity = v > 0.8 ? 2 : 0
    }
  })
  return <pointLight ref={ref} color={color} distance={5} />
}
